import { useEffect, useState, useCallback } from 'react';
import type { DraftEntityType, UserDraft } from '@gng-ehs/shared';
import { draftManager } from '../lib/draftManager.js';

interface UseDraftAutoSaveOptions {
  entityType: DraftEntityType;
  draftId: string;
  enabled: boolean;
  isDirty: boolean;
  isResumed?: boolean;
  formData: Record<string, unknown>;
  title: string;
  subtitle?: string;
}

export function useDraftAutoSave({
  entityType,
  draftId,
  enabled,
  isDirty,
  isResumed = false,
  formData,
  title,
  subtitle,
}: UseDraftAutoSaveOptions) {
  const [existingDraft, setExistingDraft] = useState<UserDraft | null>(null);

  // Check for existing draft only when creating fresh (NOT when resuming)
  useEffect(() => {
    if (enabled && !isResumed) {
      const draft = draftManager.getDraft(draftId);
      if (draft) {
        setExistingDraft(draft);
      } else {
        setExistingDraft(null);
      }
    } else {
      setExistingDraft(null);
    }
  }, [enabled, draftId, isResumed]);

  // Auto-save whenever formData changes, modal is open, and user has modified fields
  useEffect(() => {
    if (!enabled || !isDirty) return;

    draftManager.saveDraft(
      draftId,
      entityType,
      title || `New ${entityType.toUpperCase()} Draft`,
      subtitle,
      formData
    );
  }, [enabled, isDirty, formData, draftId, entityType, title, subtitle]);

  const restoreDraft = useCallback((): Record<string, unknown> | null => {
    const draft = existingDraft || draftManager.getDraft(draftId);
    setExistingDraft(null); // Dismiss banner once restored
    return draft ? draft.form_data : null;
  }, [existingDraft, draftId]);

  const discardDraft = useCallback(() => {
    draftManager.deleteDraft(draftId);
    setExistingDraft(null);
  }, [draftId]);

  const clearDraft = useCallback(() => {
    draftManager.deleteDraft(draftId);
    setExistingDraft(null);
  }, [draftId]);

  return {
    existingDraft,
    restoreDraft,
    discardDraft,
    clearDraft,
  };
}
